import { useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Menu, MenuItem, Typography, ListItemIcon } from '@mui/material';
import { useForm } from 'react-hook-form';
import { BsThreeDotsVertical } from 'react-icons/bs';
import { MdOutlineLocalPhone, MdOutlineEdit, MdDeleteOutline } from 'react-icons/md';
import Select from 'components/Common/Select';
import TelInput from 'components/Common/TelInput';
import Button from 'components/Common/Button';
import { useGetBusinessAgents } from '@/apis/business/agent/hook';
import { useGetBusinessProfile } from 'apis/business/hook';
import { useGetPublicCountries } from '@/apis/agency/businessOnboarding/hooks';
import {
  useAssignTelephonyEndpointRoles,
  useDeleteTelephonyEndpoint,
  useGetTelephonyEndpointGatewayState,
  useGetTelephonyEndpoints
} from '@/apis/business/setting/telephonyEndpoints/hook';
import { TelephonyAssignmentRole, TelephonyEndpoint } from '@/apis/business/setting/telephonyEndpoints/types';
import { queryClient } from 'providers/AllProviders';
import { countryLabel, formatNumberOrExtension } from '@/utils/phoneNumberCapabilities';
import { titleCase } from 'utils/helperFunctions';
import { toCapabilityBadges } from '../capabilityBadges';
import { defaultCountryFromNumber, showAddOnSections } from '../numberDetailFields';
import CapabilityBadgeChip from './CapabilityBadgeChip';
import EditFriendlyNameDrawer from './EditFriendlyNameDrawer';
import EditSipConnectionDrawer from './EditSipConnectionDrawer';
import ReleaseNumberConfirmDialog from './ReleaseNumberConfirmDialog';
import { formatHostSmsNumber } from './hostSmsConstants';
import TelephonyAddOns from './TelephonyAddOns';

type Props = {
  number: TelephonyEndpoint | null;
  onDeleted?: () => void;
};

type DetailFormValues = {
  inbound_call_agent: string;
  allowed_inbound_countries: string[];
  fallback_number: string;
  outbound_call_agents: string[];
  allowed_outbound_countries: string[];
  sms_endpoint_id: string;
  inbound_sms_agent: string;
  outbound_sms_agent: string;
};

const DEFAULT_ALLOWED_COUNTRIES = ['US', 'CA'];

function AgentSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <Box
      sx={{
        bgcolor: '#fff',
        border: '1px solid #E5E7EB',
        borderRadius: '6px',
        p: 2.25
      }}
    >
      <Typography fontWeight={500} fontSize={15} color='#111827' mb={2}>
        {title}
      </Typography>
      {children}
    </Box>
  );
}

function agentIdValue(assignment?: { agent_id?: number } | null) {
  return assignment?.agent_id ? String(assignment.agent_id) : '';
}

function outboundAgentIdsValue(assignment?: { agent_id?: number; agent_ids?: number[] } | null): string[] {
  if (!assignment) return [];
  if (Array.isArray(assignment.agent_ids) && assignment.agent_ids.length) {
    return assignment.agent_ids.map((id) => String(id));
  }
  return assignment.agent_id != null ? [String(assignment.agent_id)] : [];
}

function assignmentAgentIds(assignment?: { agent_id?: number; agent_ids?: number[] } | null): string[] {
  return outboundAgentIdsValue(assignment);
}

function allowedCountriesValue(assignment?: { settings?: { allowed_countries?: string[] } | null } | null) {
  const countries = assignment?.settings?.allowed_countries;
  if (!Array.isArray(countries) || countries.length === 0) return [...DEFAULT_ALLOWED_COUNTRIES];
  return countries.map((code) => String(code).toUpperCase()).filter(Boolean);
}

export default function TelephonyNumberDetail({ number, onDeleted }: Props) {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [editFriendlyNameOpen, setEditFriendlyNameOpen] = useState(false);
  const [editSipOpen, setEditSipOpen] = useState(false);
  const [releaseConfirmOpen, setReleaseConfirmOpen] = useState(false);
  const { data: businessProfile } = useGetBusinessProfile();
  const { data: agentsData } = useGetBusinessAgents({ page: 1, per_page: 500 });
  const { data: endpointsData } = useGetTelephonyEndpoints({ page: 1, per_page: 100 });
  const { data: countries = [], isLoading: isCountriesLoading } = useGetPublicCountries();
  const { mutateAsync: assignRoles, isPending: saving } = useAssignTelephonyEndpointRoles();
  const { mutateAsync: deleteEndpoint, isPending: deleting } = useDeleteTelephonyEndpoint();

  const countryOptions = useMemo(() => {
    const priorityCodes = ['US', 'CA'];
    const mapped = (countries ?? []).map((row) => ({
      value: String(row.value).toUpperCase(),
      label: String(row.label)
    }));

    return mapped.sort((a, b) => {
      const aPriority = priorityCodes.indexOf(a.value);
      const bPriority = priorityCodes.indexOf(b.value);
      if (aPriority !== -1 && bPriority !== -1) return aPriority - bPriority;
      if (aPriority !== -1) return -1;
      if (bPriority !== -1) return 1;
      return a.label.localeCompare(b.label, undefined, { numeric: true, sensitivity: 'base' });
    });
  }, [countries]);

  const { control, reset, handleSubmit, watch } = useForm<DetailFormValues>({
    defaultValues: {
      inbound_call_agent: '',
      allowed_inbound_countries: [...DEFAULT_ALLOWED_COUNTRIES],
      fallback_number: '+1',
      outbound_call_agents: [],
      allowed_outbound_countries: [...DEFAULT_ALLOWED_COUNTRIES],
      sms_endpoint_id: '',
      inbound_sms_agent: '',
      outbound_sms_agent: ''
    }
  });

  const isNetsapiensExtension = number?.kind === 'extension' && number?.provider === 'netsapiens';
  const isAsterisk = number?.provider === 'asterisk';
  const isSipProvider =
    Boolean(number) &&
    !isNetsapiensExtension &&
    !isAsterisk &&
    (number?.provider === 'sip_connectivity' || number?.provider === 'ring_central');
  const isSipRegistration =
    isSipProvider &&
    (number?.kind === 'sip_registration' ||
      number?.kind === 'extension' ||
      number?.connection_config?.type === 'sip_registration');
  const isSipTrunk =
    isSipProvider && (number?.kind === 'sip_trunk' || number?.connection_config?.type === 'sip_trunking');
  const isSipConnection = Boolean(isSipRegistration || isSipTrunk);
  const linkedSmsNumbers = useMemo(() => number?.linked_sms_numbers ?? [], [number?.linked_sms_numbers]);
  const selectedSmsId = watch('sms_endpoint_id');

  const { data: gatewayStateData } = useGetTelephonyEndpointGatewayState(number?.id, {
    enabled: Boolean(isSipRegistration && number?.id),
    refetchInterval: isSipRegistration ? 15_000 : false
  });

  useEffect(() => {
    const refreshed = gatewayStateData?.data?.endpoint;
    if (!refreshed?.id) return;
    // Only list caches: ['telephony_endpoints', params]. Prefix also matches detail /
    // available_numbers / gateway_state where `data` is not an endpoint array.
    queryClient.setQueriesData(
      {
        predicate: (query) =>
          query.queryKey[0] === 'telephony_endpoints' &&
          query.queryKey.length === 2 &&
          typeof query.queryKey[1] === 'object'
      },
      (current: unknown) => {
        if (!current || typeof current !== 'object' || !('data' in current)) return current;
        const list = current as { data: unknown; meta?: unknown };
        if (!Array.isArray(list.data)) return current;
        return {
          ...list,
          data: list.data.map((row: TelephonyEndpoint) => (row?.id === refreshed.id ? { ...row, ...refreshed } : row))
        };
      }
    );
  }, [gatewayStateData]);

  const agentsInUseByRole = useMemo(() => {
    const map: Record<TelephonyAssignmentRole, Set<string>> = {
      inbound_call: new Set(),
      outbound_call: new Set(),
      inbound_sms: new Set(),
      outbound_sms: new Set()
    };
    (endpointsData?.data ?? []).forEach((endpoint) => {
      if (!number?.id || endpoint.id === number.id) return;
      (Object.keys(map) as TelephonyAssignmentRole[]).forEach((role) => {
        assignmentAgentIds(endpoint.assignments?.[role]).forEach((agentId) => map[role].add(agentId));
      });
    });
    return map;
  }, [endpointsData, number?.id]);

  const agentOptionsForRole = useMemo(() => {
    const build = (
      role: TelephonyAssignmentRole,
      { multiple = false, interaction }: { multiple?: boolean; interaction?: 'inbound' | 'outbound' } = {}
    ) => {
      const inUse = agentsInUseByRole[role];
      const options: { value: string; label: string; disabled?: boolean }[] = multiple
        ? []
        : [{ value: '', label: 'Select' }];
      (agentsData?.data ?? []).forEach((agent) => {
        // Call roles: only agents whose agent_interaction matches inbound vs outbound.
        if (interaction && agent.agent_interaction !== interaction) return;
        const value = String(agent.id);
        const taken = inUse.has(value);
        const label = agent.location_name ? `${agent.name} - ${agent.location_name}` : agent.name;
        options.push({
          value,
          label,
          disabled: taken
        });
      });
      const assignments = number?.assignments;
      if (assignments) {
        // Keep currently assigned agents visible even if interaction no longer matches.
        assignmentAgentIds(assignments[role]).forEach((value) => {
          if (options.some((opt) => opt.value === value)) return;
          const row = assignments[role];
          const taken = inUse.has(value);
          const named = row?.agents?.find((a) => String(a.id) === value)?.name;
          options.push({
            value,
            label: named || row?.agent_name || `Agent ${value}`,
            disabled: taken
          });
        });
      }
      return options;
    };
    return {
      inbound_call: build('inbound_call', { interaction: 'inbound' }),
      outbound_call: build('outbound_call', { multiple: true, interaction: 'outbound' }),
      inbound_sms: build('inbound_sms'),
      outbound_sms: build('outbound_sms')
    };
  }, [agentsData, agentsInUseByRole, number]);

  const smsNumberOptions = useMemo(() => {
    const options = [{ value: '', label: 'Select' }];
    linkedSmsNumbers.forEach((row) => {
      options.push({
        value: String(row.id),
        label: formatNumberOrExtension({ phone_number: row.phone_number, kind: 'purchased_did' }) || row.phone_number
      });
    });
    return options;
  }, [linkedSmsNumbers]);

  const capabilitySet = new Set((number?.capabilities ?? []).map((c) => c.toLowerCase()));
  const hasSmsCapability = capabilitySet.has('sms') || capabilitySet.has('hosted_sms') || capabilitySet.has('a2p');
  const smsCapable = isAsterisk
    ? false
    : isNetsapiensExtension
      ? linkedSmsNumbers.length > 0 && Boolean(selectedSmsId)
      : hasSmsCapability;
  const showSmsSections = !isAsterisk && (isNetsapiensExtension || hasSmsCapability);
  const showAddOns = !isNetsapiensExtension && !isAsterisk && showAddOnSections(number);

  useEffect(() => {
    if (!number) return;
    const assignments = number.assignments ?? {};
    reset({
      inbound_call_agent: agentIdValue(assignments.inbound_call),
      allowed_inbound_countries: allowedCountriesValue(assignments.inbound_call),
      fallback_number: assignments.inbound_call?.settings?.fallback_number || '+1',
      outbound_call_agents: outboundAgentIdsValue(assignments.outbound_call),
      allowed_outbound_countries: allowedCountriesValue(assignments.outbound_call),
      sms_endpoint_id: number.selected_sms_endpoint_id ? String(number.selected_sms_endpoint_id) : '',
      inbound_sms_agent: agentIdValue(assignments.inbound_sms),
      outbound_sms_agent: agentIdValue(assignments.outbound_sms)
    });
  }, [number, reset]);

  const assignedToAgents = useMemo(() => {
    if (!number?.assignments) return false;
    return Object.values(number.assignments).some(
      (row) => Boolean(row?.agent_id) || (Array.isArray(row?.agent_ids) && row.agent_ids.length > 0)
    );
  }, [number?.assignments]);

  const releaseNumberLabel = useMemo(() => {
    if (!number) return '';
    if (number.kind === 'extension' || number.kind === 'sip_registration' || number.kind === 'sip_trunk') {
      return formatNumberOrExtension(number);
    }
    return formatHostSmsNumber(number.phone_number) || formatNumberOrExtension(number) || number.phone_number;
  }, [number]);

  const openReleaseConfirm = () => {
    setMenuAnchor(null);
    setReleaseConfirmOpen(true);
  };

  const onReleaseConfirm = async () => {
    if (!number) return;
    await deleteEndpoint(number.id);
    setReleaseConfirmOpen(false);
    onDeleted?.();
  };

  if (!number) {
    return (
      <Box
        flex={1}
        minWidth={0}
        display='flex'
        alignItems='center'
        justifyContent='center'
        bgcolor='#fff'
        borderRadius='6px'
        border='1px solid #E5E7EB'
        minHeight={320}
      >
        <Typography color='#6B7280'>Select a number to view configuration.</Typography>
      </Box>
    );
  }

  const badges = toCapabilityBadges(number.capabilities);
  const provider = number.provider ? titleCase(number.provider.replace(/_/g, ' ')) : '';
  const region = countryLabel(number.country_code);
  const providerLine = [provider, region].filter(Boolean).join(' · ');
  const phoneNumberLabel = formatNumberOrExtension(number);
  const friendlyName = number.friendly_name?.trim() || '';
  const headerTitle = isAsterisk ? friendlyName || phoneNumberLabel : phoneNumberLabel;
  const showFriendlyNameSubtitle = Boolean(friendlyName) && friendlyName !== headerTitle;

  const onSave = handleSubmit(async (values) => {
    await assignRoles({
      id: number.id,
      payload: {
        assignments: {
          ...(isNetsapiensExtension ? { sms_endpoint_id: values.sms_endpoint_id || null } : {}),
          inbound_call: {
            agent_id: values.inbound_call_agent || null,
            fallback_number: values.fallback_number,
            allowed_countries: values.allowed_inbound_countries ?? []
          },
          outbound_call: {
            agent_ids: (values.outbound_call_agents ?? []).filter(Boolean),
            agent_id: values.outbound_call_agents?.[0] || null,
            allowed_countries: values.allowed_outbound_countries ?? []
          },
          inbound_sms: { agent_id: values.inbound_sms_agent || null },
          outbound_sms: { agent_id: values.outbound_sms_agent || null }
        }
      }
    });
  });

  return (
    <Box flex={1} minWidth={0} display='flex' flexDirection='column' gap={2} pb={4}>
      <Box bgcolor='#fff' borderRadius='6px' border='1px solid #E5E7EB' p={2.5} sx={{ overflow: 'visible' }}>
        <Box display='flex' alignItems='flex-start' justifyContent='space-between' gap={2}>
          <Box display='flex' alignItems='center' gap={1.75}>
            <Box
              sx={{
                width: 48,
                height: 48,
                borderRadius: '50%',
                bgcolor: (theme) => theme.palette.primary.light,
                color: 'primary.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}
            >
              <MdOutlineLocalPhone size={24} />
            </Box>
            <Box>
              <Typography fontWeight={500} fontSize={20} color='#111827'>
                {headerTitle}
              </Typography>
              {providerLine && (
                <Typography fontSize={13} color='#6B7280' mt={0.25}>
                  {providerLine}
                </Typography>
              )}
              {showFriendlyNameSubtitle && (
                <Typography fontSize={12} color='#9CA3AF' mt={0.25}>
                  {friendlyName}
                </Typography>
              )}
            </Box>
          </Box>
          <Box display='flex' alignItems='center' gap={1} sx={{ position: 'relative', zIndex: 2 }}>
            {badges.length > 0 && (
              <Box display='flex' flexWrap='wrap' gap={0.6} justifyContent='flex-end'>
                {badges.map((badge) => (
                  <CapabilityBadgeChip key={badge} badge={badge} />
                ))}
              </Box>
            )}
            <IconButton onClick={(e) => setMenuAnchor(e.currentTarget)} size='small'>
              <BsThreeDotsVertical />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={() => setMenuAnchor(null)}
        disablePortal={false}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{
          paper: {
            sx: {
              mt: 0.5,
              minWidth: 150,
              borderRadius: '8px',
              border: '1px solid #E5E7EB',
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
              zIndex: (theme) => theme.zIndex.modal
            }
          }
        }}
        sx={{ zIndex: (theme) => theme.zIndex.modal }}
      >
        <MenuItem
          onClick={() => {
            setMenuAnchor(null);
            if (isSipConnection) {
              setEditSipOpen(true);
            } else {
              setEditFriendlyNameOpen(true);
            }
          }}
        >
          <ListItemIcon>
            <MdOutlineEdit size={18} />
          </ListItemIcon>
          Edit
        </MenuItem>
        {!isNetsapiensExtension && !isAsterisk && (
          <MenuItem onClick={openReleaseConfirm} disabled={deleting} sx={{ color: 'error.main' }}>
            <ListItemIcon sx={{ color: 'error.main' }}>
              <MdDeleteOutline size={18} />
            </ListItemIcon>
            Release Number
          </MenuItem>
        )}
      </Menu>

      <AgentSection title='Inbound Call Agent'>
        <Box display='flex' flexDirection='column' gap={2}>
          <Select
            sortOrder='none'
            name='inbound_call_agent'
            control={control}
            label='Call Agent'
            options={agentOptionsForRole.inbound_call}
            fullWidth
            disabled={isAsterisk}
          />
          <Select
            sortOrder='none'
            name='allowed_inbound_countries'
            control={control}
            label='Allowed Inbound Countries'
            options={countryOptions}
            multiple
            allSelect
            searchable
            searchPlaceholder='Search countries'
            isLoading={isCountriesLoading}
            fullWidth
          />
          <Box>
            <TelInput
              name='fallback_number'
              control={control}
              label='Fallback Number'
              fullWidth
              defaultCountry={defaultCountryFromNumber(number)}
              preferredCountries={['US', 'CA', 'IE', 'GB']}
              InputLabelProps={{ shrink: true }}
            />
            <Typography fontSize={12} color='#6B7280' mt={1}>
              When inbound call concurrency is reached and cannot free up after extended ringing, calls fall back to
              this number.
            </Typography>
          </Box>
        </Box>
      </AgentSection>

      <AgentSection title='Outbound Call Agent'>
        <Box display='flex' flexDirection='column' gap={2}>
          <Select
            sortOrder='none'
            name='outbound_call_agents'
            control={control}
            label='Call Agent'
            options={agentOptionsForRole.outbound_call}
            multiple
            fullWidth
            disabled={isAsterisk}
          />
          <Select
            sortOrder='none'
            name='allowed_outbound_countries'
            control={control}
            label='Allowed Outbound Countries'
            options={countryOptions}
            multiple
            allSelect
            searchable
            searchPlaceholder='Search countries'
            isLoading={isCountriesLoading}
            fullWidth
          />
        </Box>
      </AgentSection>

      {isNetsapiensExtension && (
        <AgentSection title='Enable SMS'>
          <Typography fontSize={13} color='#6B7280' mb={1.5}>
            Select an SMS number linked to this extension.
          </Typography>
          <Select
            sortOrder='none'
            name='sms_endpoint_id'
            control={control}
            label='SMS Number *'
            options={smsNumberOptions}
            fullWidth
            disabled={linkedSmsNumbers.length === 0}
          />
          {linkedSmsNumbers.length === 0 && (
            <Typography fontSize={12} color='#9CA3AF' mt={1}>
              No SMS numbers are linked to this extension yet. Re-run NetSapiens sync after SMS numbers are assigned in
              the carrier portal.
            </Typography>
          )}
        </AgentSection>
      )}

      {showSmsSections && (
        <AgentSection title='Inbound SMS Agent'>
          <Select
            sortOrder='none'
            name='inbound_sms_agent'
            control={control}
            label='SMS Agent'
            options={agentOptionsForRole.inbound_sms}
            fullWidth
            disabled={!smsCapable}
          />
          {!smsCapable && (
            <Typography fontSize={12} color='#9CA3AF' mt={1}>
              {isNetsapiensExtension
                ? 'Select an SMS number above to assign SMS agents.'
                : "SMS is not available for this number's country or capabilities."}
            </Typography>
          )}
        </AgentSection>
      )}

      {showSmsSections && (
        <AgentSection title='Outbound SMS Agent'>
          <Select
            sortOrder='none'
            name='outbound_sms_agent'
            control={control}
            label='SMS Agent'
            options={agentOptionsForRole.outbound_sms}
            fullWidth
            disabled={!smsCapable}
          />
        </AgentSection>
      )}

      <Box display='flex' justifyContent='flex-end' gap={1.5}>
        <Button
          variant='outlined'
          color='inherit'
          onClick={() => {
            const assignments = number.assignments ?? {};
            reset({
              inbound_call_agent: agentIdValue(assignments.inbound_call),
              allowed_inbound_countries: allowedCountriesValue(assignments.inbound_call),
              fallback_number: assignments.inbound_call?.settings?.fallback_number || '+1',
              outbound_call_agents: outboundAgentIdsValue(assignments.outbound_call),
              allowed_outbound_countries: allowedCountriesValue(assignments.outbound_call),
              sms_endpoint_id: number.selected_sms_endpoint_id ? String(number.selected_sms_endpoint_id) : '',
              inbound_sms_agent: agentIdValue(assignments.inbound_sms),
              outbound_sms_agent: agentIdValue(assignments.outbound_sms)
            });
          }}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '999px',
            px: 2.5,
            borderColor: '#E5E7EB',
            color: 'text.primary',
            bgcolor: '#fff',
            boxShadow: 'none',
            '&:hover': { borderColor: '#E5E7EB', bgcolor: '#F9FAFB' }
          }}
        >
          Cancel
        </Button>
        <Button
          variant='contained'
          color='primary'
          onClick={onSave}
          isPending={saving}
          sx={{
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '999px',
            px: 2.5,
            boxShadow: 'none',
            '&:hover': { boxShadow: 'none' }
          }}
        >
          Save Changes
        </Button>
      </Box>

      {showAddOns && <TelephonyAddOns preferredEndpoint={number} />}

      <EditFriendlyNameDrawer
        open={editFriendlyNameOpen}
        endpoint={number}
        onClose={() => setEditFriendlyNameOpen(false)}
      />
      <EditSipConnectionDrawer open={editSipOpen} endpoint={number} onClose={() => setEditSipOpen(false)} />
      <ReleaseNumberConfirmDialog
        open={releaseConfirmOpen}
        phoneNumberLabel={releaseNumberLabel}
        businessName={businessProfile?.name}
        assignedToAgents={assignedToAgents}
        isPending={deleting}
        onCancel={() => setReleaseConfirmOpen(false)}
        onConfirm={onReleaseConfirm}
      />
    </Box>
  );
}
